// src/features/Analytics/WhatIfAnalysisPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  PlayArrow,
  Refresh,
  TrendingUp
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import useMLPredictions from './hooks/useMLPredictions';

const WhatIfAnalysisPage = () => {
  const { t } = useI18n();
  const { makePrediction, loading: mlLoading } = useMLPredictions();
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({
    name: '',
    parameters: {
      temperature: 25,
      pressure: 100,
      speed: 50,
      load: 75,
      quality: 85
    }
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const parametersConfig = [
    {
      key: 'temperature',
      label: t('whatIf.temperature') || 'Температура',
      min: 0,
      max: 100,
      unit: '°C'
    },
    {
      key: 'pressure',
      label: t('whatIf.pressure') || 'Тиск',
      min: 0,
      max: 200,
      unit: 'kPa'
    },
    {
      key: 'speed',
      label: t('whatIf.speed') || 'Швидкість',
      min: 0,
      max: 100,
      unit: '%'
    },
    {
      key: 'load',
      label: t('whatIf.load') || 'Навантаження',
      min: 0,
      max: 100,
      unit: '%'
    },
    {
      key: 'quality',
      label: t('whatIf.quality') || 'Якість',
      min: 0,
      max: 100,
      unit: '%'
    }
  ];

  const runAnalysis = async () => {
    if (!currentScenario.name.trim()) {
      alert(t('whatIf.enterScenarioName') || 'Введіть назву сценарію');
      return;
    }

    setLoading(true);
    try {
      const inputData = [
        currentScenario.parameters.temperature,
        currentScenario.parameters.pressure,
        currentScenario.parameters.speed,
        currentScenario.parameters.load,
        currentScenario.parameters.quality
      ];

      const prediction = await makePrediction(inputData);

      const result = {
        id: Date.now(),
        scenarioName: currentScenario.name,
        parameters: { ...currentScenario.parameters },
        prediction: prediction?.output || 0,
        confidence: prediction?.confidence || 0,
        timestamp: new Date().toISOString()
      };

      setResults(result);
      setHistory(prev => [result, ...prev.slice(0, 9)]); // Зберігаємо останні 10 результатів

      // Зберігаємо сценарій
      setScenarios(prev => [...prev, {
        id: Date.now(),
        ...currentScenario
      }]);

    } catch (error) {
      console.error('Помилка аналізу:', error);
      alert(t('whatIf.analysisError') || 'Помилка виконання аналізу');
    } finally {
      setLoading(false);
    }
  };

  const handleParameterChange = (parameter, value) => {
    setCurrentScenario(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [parameter]: value
      }
    }));
  };

  const loadScenario = (scenario) => {
    setCurrentScenario({
      name: scenario.name,
      parameters: { ...scenario.parameters }
    });
  };

  const resetScenario = () => {
    setCurrentScenario({
      name: '',
      parameters: {
        temperature: 25,
        pressure: 100,
        speed: 50,
        load: 75,
        quality: 85
      }
    });
    setResults(null);
  };

  const calculateImprovement = () => {
    if (!results || history.length < 2) return 0;
    
    const currentValue = results.prediction;
    const previousValue = history[1]?.prediction || currentValue;
    
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        <TrendingUp sx={{ mr: 2, verticalAlign: 'middle' }} />
        {t('whatIf.title') || 'What-If Аналіз'}
      </Typography>

      <Grid container spacing={3}>
        {/* Панель параметрів */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('whatIf.scenarioParameters') || 'Параметри сценарію'}
              </Typography>

              <TextField
                fullWidth
                label={t('whatIf.scenarioName') || 'Назва сценарію'}
                value={currentScenario.name}
                onChange={(e) => setCurrentScenario(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
              />

              {parametersConfig.map((param) => (
                <Box key={param.key} sx={{ mt: 2 }}>
                  <Typography gutterBottom>
                    {param.label}: {currentScenario.parameters[param.key]} {param.unit}
                  </Typography>
                  <Slider
                    value={currentScenario.parameters[param.key]}
                    onChange={(_, value) => handleParameterChange(param.key, value)}
                    min={param.min}
                    max={param.max}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}${param.unit}`}
                  />
                </Box>
              ))}

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={runAnalysis}
                  disabled={loading || mlLoading}
                >
                  {t('whatIf.runAnalysis') || 'Запустити аналіз'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={resetScenario}
                >
                  {t('whatIf.reset') || 'Скинути'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Історія сценаріїв */}
          {scenarios.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('whatIf.savedScenarios') || 'Збережені сценарії'}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('whatIf.name') || 'Назва'}</TableCell>
                        <TableCell>{t('whatIf.actions') || 'Дії'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scenarios.map((scenario) => (
                        <TableRow key={scenario.id}>
                          <TableCell>{scenario.name}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => loadScenario(scenario)}
                            >
                              {t('whatIf.load') || 'Завантажити'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Результати */}
        <Grid item xs={12} md={6}>
          {loading || mlLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : results ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('whatIf.results') || 'Результати аналізу'}
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="h6">
                    {t('whatIf.predictedOutput') || 'Прогнозований результат'}: {results.prediction.toFixed(2)}
                  </Typography>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('whatIf.confidence') || 'Довіра'}
                    </Typography>
                    <Typography variant="h6">
                      {(results.confidence * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('whatIf.improvement') || 'Покращення'}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color={calculateImprovement() >= 0 ? 'success.main' : 'error.main'}
                    >
                      {calculateImprovement().toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>

                {/* Детальні параметри */}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  {t('whatIf.parameters') || 'Параметри сценарію'}:
                </Typography>
                <Grid container spacing={1}>
                  {parametersConfig.map((param) => (
                    <Grid item xs={6} key={param.key}>
                      <Typography variant="body2">
                        {param.label}: {results.parameters[param.key]} {param.unit}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('whatIf.noResults') || 'Результати відсутні'}
                </Typography>
                <Alert severity="info">
                  {t('whatIf.runAnalysisFirst') || 'Запустіть аналіз для перегляду результатів'}
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* Історія аналізів */}
          {history.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('whatIf.analysisHistory') || 'Історія аналізів'}
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('whatIf.scenario') || 'Сценарій'}</TableCell>
                        <TableCell align="right">{t('whatIf.result') || 'Результат'}</TableCell>
                        <TableCell align="right">{t('whatIf.confidence') || 'Довіра'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.scenarioName}</TableCell>
                          <TableCell align="right">{item.prediction.toFixed(2)}</TableCell>
                          <TableCell align="right">{(item.confidence * 100).toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default WhatIfAnalysisPage;


